import React, { useEffect, lazy, Suspense } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { store } from './store';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store';
import { setAuthUser, setUser, setOrganization, setLoading } from './store/slices/authSlice';
import { setItems } from './store/slices/inventorySlice';
import { setEvents } from './store/slices/historySlice';
import { AuthService } from './firebase/authService';
import { FirestoreService } from './firebase/firestoreService';
import { InventoryItem, HistoryEvent } from './types/models';
import AppLayout from './components/Layout/AppLayout';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded pages for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const PendingApprovalPage = lazy(() => import('./pages/PendingApprovalPage'));

const PageLoader: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    Loading...
  </div>
);

const AppContent: React.FC = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, loading, user, organization } = useSelector((state: RootState) => state.auth);

  // Set up inventory and events subscriptions when authenticated
  // Re-subscribes when organization changes to scope data appropriately
  useEffect(() => {
    if (isAuthenticated) {
      const orgId = organization?.id;

      // No org means demo/anonymous user — show sample data
      if (!orgId) {
        const now = Date.now() / 1000;
        const sampleItems: InventoryItem[] = [
          {
            id: 'demo-1', itemID: 'demo-1', itemName: 'MacBook Pro 14"',
            itemQRFileName: '', checkedOut: true, checkOutHistory: [],
            itemGroup: 'Electronics', checkedOutTo: 'Sarah Johnson',
            checkedOutDate: now - 86400, lastUpdated: now - 86400,
          },
          {
            id: 'demo-2', itemID: 'demo-2', itemName: 'Projector - Epson',
            itemQRFileName: '', checkedOut: false, checkOutHistory: [],
            itemGroup: 'Electronics', lastUpdated: now - 3600,
          },
          {
            id: 'demo-3', itemID: 'demo-3', itemName: 'Cordless Drill',
            itemQRFileName: '', checkedOut: true, checkOutHistory: [],
            itemGroup: 'Tools', checkedOutTo: 'Mike Chen',
            checkedOutDate: now - 172800, lastUpdated: now - 172800,
          },
          {
            id: 'demo-4', itemID: 'demo-4', itemName: 'First Aid Kit',
            itemQRFileName: '', checkedOut: false, checkOutHistory: [],
            itemGroup: 'Safety', lastUpdated: now - 7200,
          },
          {
            id: 'demo-5', itemID: 'demo-5', itemName: 'Canon EOS R6 Camera',
            itemQRFileName: '', checkedOut: false, checkOutHistory: [],
            itemGroup: 'Electronics', lastUpdated: now - 600,
          },
        ];
        const sampleEvents: HistoryEvent[] = [
          { eventID: 'evt-1', eventDate: now - 86400, userName: 'Sarah Johnson', checkedOut: true, itemId: 'demo-1', itemName: 'MacBook Pro 14"' },
          { eventID: 'evt-2', eventDate: now - 172800, userName: 'Mike Chen', checkedOut: true, itemId: 'demo-3', itemName: 'Cordless Drill' },
          { eventID: 'evt-3', eventDate: now - 3600, userName: 'Sarah Johnson', checkedOut: false, itemId: 'demo-2', itemName: 'Projector - Epson' },
          { eventID: 'evt-4', eventDate: now - 259200, userName: 'Alex Rivera', checkedOut: true, itemId: 'demo-2', itemName: 'Projector - Epson' },
          { eventID: 'evt-5', eventDate: now - 345600, userName: 'Alex Rivera', checkedOut: false, itemId: 'demo-5', itemName: 'Canon EOS R6 Camera' },
        ];
        dispatch(setItems(sampleItems));
        dispatch(setEvents(sampleEvents));
        return;
      }

      const unsubInventory = FirestoreService.subscribeToInventoryItems((items) => {
        dispatch(setItems(items));
      }, orgId);

      const unsubEvents = FirestoreService.subscribeToAllEvents((events) => {
        dispatch(setEvents(events));
      }, orgId);

      return () => {
        unsubInventory();
        unsubEvents();
      };
    }
  }, [dispatch, isAuthenticated, organization?.id]);

  useEffect(() => {
    dispatch(setLoading(true));

    // Listen for auth state changes
    const unsubscribe = AuthService.onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        const authUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          emailVerified: firebaseUser.emailVerified,
        };

        dispatch(setAuthUser(authUser));

        try {
          const user = await AuthService.getCurrentUser();
          dispatch(setUser(user));

          if (user?.organizationID) {
            const organization = await AuthService.getUserOrganization(user.userID);
            dispatch(setOrganization(organization));
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        dispatch(setAuthUser(null));
        dispatch(setUser(null));
        dispatch(setOrganization(null));
      }
      
      dispatch(setLoading(false));
    });

    return () => unsubscribe();
  }, [dispatch]);

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    );
  }

  // Pending approval: user signed up with invite code, waiting for admin
  if (user?.memberStatus === 'pending') {
    return (
      <Suspense fallback={<PageLoader />}>
        <PendingApprovalPage />
      </Suspense>
    );
  }

  // Rejected: show message with sign out
  if (user?.memberStatus === 'rejected') {
    return (
      <Suspense fallback={<PageLoader />}>
        <PendingApprovalPage />
      </Suspense>
    );
  }

  return (
    <AppLayout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <Router>
          <AppContent />
        </Router>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;